/**
 * manual-quote-editor-page.tsx — Lập / sửa báo giá thủ công. Route `/platform/manual-quotes/new` và `/platform/manual-quotes/:id`.
 *
 * Nội dung theo mẫu docs/mau-bieu/don-dat-hang-thu-cong.html (khách hàng, nội dung đặt hàng, dịch vụ khác, mã ưu đãi,
 * thanh toán, điều khoản) nhưng giá KHÔNG nhập tay: mỗi lần đổi form, server tính lại (POST /preview) theo bảng giá
 * hiện hành + chính sách mua của tổ chức — đúng số khách sẽ trả nếu mua trực tuyến (cộng giảm thêm / dịch vụ khác nếu có).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { AlertTriangle, ArrowLeft, Ban, CheckCircle2, Copy, Loader2, Plus, Printer, Save, Search, Send, Trash2, X } from 'lucide-react'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox, Textarea } from '@/components/ui/misc'
import { platformApiError } from '@/lib/platform-client'
import { cn } from '@/lib/utils'
import { usePlatformPricing } from '@/hooks/use-platform-pricing'
import {
  loadOrgContext, previewManualQuote, searchQuoteOrgs, useManualQuote, useQuoteAction, useQuoteTemplate, useSaveManualQuote,
} from '@/hooks/use-manual-quotes'
import { QUOTA_KEYS, QUOTA_LABELS } from '../pricing-editor'
import { CancelQuoteDialog, ConfirmPaidDialog } from './manual-quote-dialogs'
import {
  applyPlanDefaults, daySuggestions, emptyForm, endDate, fmt, formToInput, MODE_LABELS, ORDER_TYPE_LABELS, POLICY_CODES,
  quantitiesOf, quoteToForm, sellablePlans, STATUS_LABELS, STATUS_VARIANT, viDate,
  type Breakdown, type Customer, type OrderType, type OrgContext, type QuoteForm,
} from './manual-quote-helpers'

const selectCls = 'flex h-8 w-full rounded-md border border-input bg-card px-2 py-1 text-sm hover:border-primary/60 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/15 disabled:opacity-50'

function Section({ title, children, className }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <section className={cn('space-y-3 rounded-lg border bg-card p-4', className)}>
      <h2 className="text-xs font-semibold uppercase tracking-wide text-primary">{title}</h2>
      {children}
    </section>
  )
}

export function ManualQuoteEditorPage() {
  const { id } = useParams()
  const isNew = !id || id === 'new'
  const existing = useManualQuote(isNew ? undefined : id)
  const pricing = usePlatformPricing()
  const template = useQuoteTemplate()
  if (!isNew && existing.isError) return <ErrorState message="Không tải được báo giá" />
  if ((!isNew && !existing.data) || !pricing.data || !template.data) return <Loading label="Đang tải..." />
  return <Editor key={existing.data?.updatedAt ?? 'new'} quoteId={isNew ? null : id!} />
}

function Editor({ quoteId }: { quoteId: string | null }) {
  const navigate = useNavigate()
  const pricing = usePlatformPricing().data!.pricing
  const tpl = useQuoteTemplate().data!.template
  const existing = useManualQuote(quoteId ?? undefined).data
  const [form, setForm] = useState<QuoteForm>(() => (existing ? quoteToForm(existing) : emptyForm(pricing, tpl)))
  const [ctx, setCtx] = useState<OrgContext | null>(null)
  const [preview, setPreview] = useState<{ data: Breakdown | null; error: { message: string; code?: string } | null; loading: boolean }>({ data: null, error: null, loading: true })
  const [paying, setPaying] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const save = useSaveManualQuote()
  const action = useQuoteAction()
  const status = existing?.displayStatus ?? 'draft'
  const locked = !!existing && (existing.status === 'paid' || existing.status === 'cancelled')
  const plans = useMemo(() => sellablePlans(pricing), [pricing])
  const set = (patch: Partial<QuoteForm>) => setForm((f) => ({ ...f, ...patch }))
  const setCustomer = (patch: Partial<Customer>) => setForm((f) => ({ ...f, customer: { ...f.customer, ...patch } }))

  // Ngữ cảnh tổ chức (gói / hạn / số lượng đang hiệu lực) khi mở báo giá đã có tổ chức.
  useEffect(() => {
    if (form.orgId && !ctx) loadOrgContext(form.orgId).then(setCtx).catch(() => undefined)
  }, [form.orgId, ctx])

  // Server tính lại sau mỗi lần đổi form (debounce 400ms, bỏ kết quả cũ).
  const seq = useRef(0)
  const input = useMemo(() => formToInput(form), [form])
  useEffect(() => {
    const n = ++seq.current
    setPreview((p) => ({ ...p, loading: true }))
    const t = setTimeout(() => {
      previewManualQuote(input)
        .then((data) => { if (n === seq.current) setPreview({ data, error: null, loading: false }) })
        .catch((err) => {
          if (n !== seq.current) return
          const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code
          setPreview({ data: null, error: { message: platformApiError(err), code }, loading: false })
        })
    }, 400)
    return () => clearTimeout(t)
  }, [input])

  const pickOrg = async (orgId: string) => {
    try {
      const c = await loadOrgContext(orgId)
      setCtx(c)
      const plan = plans.find((p) => p.id === c.effectivePlan)
      setForm((f) => {
        let next: QuoteForm = {
          ...f, orgId, orgName: c.org.name, orderType: c.suggestedType,
          customer: { ...f.customer, name: f.customer.name || c.org.name, email: f.customer.email || c.org.ownerEmail || '', contact: f.customer.contact || c.org.ownerName || '' },
        }
        if (plan) next = applyPlanDefaults({ ...next, quantities: c.purchased ? quantitiesOf(c.purchased) : next.quantities }, plan)
        return next
      })
    } catch (err) {
      toast.error(platformApiError(err))
    }
  }

  const doSave = () => save.mutate({ id: quoteId ?? undefined, input }, {
    onSuccess: (q) => {
      toast.success(quoteId ? 'Đã lưu báo giá' : `Đã lập báo giá ${q.number}`)
      if (!quoteId) navigate(`/platform/manual-quotes/${q.id}`, { replace: true })
    },
    onError: (err) => toast.error(platformApiError(err)),
  })
  const doAction = (a: 'send' | 'duplicate') => quoteId && action.mutate({ id: quoteId, action: a }, {
    onSuccess: (q) => {
      if (a === 'duplicate') { toast.success(`Đã tạo bản sao ${q.number}`); navigate(`/platform/manual-quotes/${q.id}`) }
      else toast.success('Đã đánh dấu đã gửi')
    },
    onError: (err) => toast.error(platformApiError(err)),
  })

  const b = preview.data
  const days = Number(form.days) || 0
  const isPolicyError = !!preview.error?.code && POLICY_CODES.has(preview.error.code)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" asChild><Link to="/platform/manual-quotes"><ArrowLeft className="h-4 w-4" /> Danh sách</Link></Button>
        <h1 className="text-lg font-medium">{existing ? `Báo giá ${existing.number}` : 'Lập báo giá mới'}</h1>
        {existing && <Badge variant={STATUS_VARIANT[status]}>{STATUS_LABELS[status]}</Badge>}
        <div className="ml-auto flex flex-wrap gap-2">
          {existing && (
            <Button variant="outline" size="sm" asChild>
              <a href={`/platform/manual-quotes/${existing.id}/print`} target="_blank" rel="noopener"><Printer className="h-4 w-4" /> In / Lưu PDF</a>
            </Button>
          )}
          {existing && <Button variant="outline" size="sm" onClick={() => doAction('duplicate')}><Copy className="h-4 w-4" /> Sao chép</Button>}
          {existing?.displayStatus === 'draft' && <Button variant="outline" size="sm" onClick={() => doAction('send')}><Send className="h-4 w-4" /> Đã gửi khách</Button>}
          {existing && !locked && <Button variant="outline" size="sm" onClick={() => setCancelling(true)}><Ban className="h-4 w-4" /> Huỷ</Button>}
          {existing && !locked && <Button variant="outline" size="sm" onClick={() => setPaying(true)}><CheckCircle2 className="h-4 w-4" /> Xác nhận đã thanh toán</Button>}
          {!locked && (
            <Button size="sm" onClick={doSave} disabled={save.isPending || !!preview.error}>
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {quoteId ? 'Lưu' : 'Lập báo giá'}
            </Button>
          )}
        </div>
      </div>

      {existing && locked && (
        <div className="rounded-lg border bg-muted/40 px-4 py-3 text-sm">
          {existing.status === 'paid' ? (
            <>
              Đã thanh toán {existing.paidAt ? `ngày ${viDate(existing.paidAt)}` : ''} (mã GD {existing.paymentRef}).{' '}
              {existing.orgId && <Link className="text-primary hover:underline" to={`/platform/companies/${existing.orgId}?tab=billing`}>Xem tổ chức{existing.org ? ` ${existing.org.name}` : ''} / đơn</Link>}
              {existing.orgCreated && ' — tổ chức được tạo từ báo giá này.'}
              {existing.order?.periodEnd && ` Kỳ dịch vụ tới ${viDate(existing.order.periodEnd)}.`}
            </>
          ) : (
            <>Đã huỷ{existing.cancelReason ? `: ${existing.cancelReason}` : ''}. Bấm "Sao chép" để lập báo giá mới từ nội dung này.</>
          )}
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
        <fieldset disabled={locked} className="min-w-0 space-y-4">
          <Section title="1. Khách hàng (bên mua)">
            <OrgPicker
              orgId={form.orgId}
              orgName={form.orgName}
              onPick={pickOrg}
              onClear={() => { setCtx(null); set({ orgId: null, orgName: '', orderType: form.orderType === 'addon' ? 'new' : form.orderType, override: false }) }}
            />
            {ctx && (
              <p className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                Đang dùng: <b>{ctx.planName ?? ctx.plan ?? 'Miễn phí'}</b>
                {ctx.expiresAt ? ` · hết hạn ${viDate(ctx.expiresAt)}` : ''}
                {ctx.purchased ? ` · đang có ${QUOTA_KEYS.map((k) => `${ctx.purchased![k]} ${QUOTA_LABELS[k].toLowerCase()}`).join(', ')}` : ''}
                {ctx.usage ? ` · đang dùng ${QUOTA_KEYS.map((k) => `${ctx.usage![k]} ${QUOTA_LABELS[k].toLowerCase()}`).join(', ')}` : ''}
                {ctx.upgradeOnly ? ' · gói còn hạn: chỉ nâng gói / tăng số lượng (như mua trực tuyến)' : ''}
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Tên đơn vị / cá nhân" wide><Input value={form.customer.name} onChange={(e) => setCustomer({ name: e.target.value })} placeholder="Công ty TNHH ..." /></Field>
              <Field label="Mã số thuế"><Input value={form.customer.taxCode} onChange={(e) => setCustomer({ taxCode: e.target.value })} placeholder="Nếu có" /></Field>
              <Field label="Người đại diện"><Input value={form.customer.representative} onChange={(e) => setCustomer({ representative: e.target.value })} placeholder="Họ tên, chức vụ" /></Field>
              <Field label="Địa chỉ" wide><Input value={form.customer.address} onChange={(e) => setCustomer({ address: e.target.value })} placeholder="Địa chỉ xuất hoá đơn" /></Field>
              <Field label="Người liên hệ"><Input value={form.customer.contact} onChange={(e) => setCustomer({ contact: e.target.value })} /></Field>
              <Field label="Điện thoại"><Input value={form.customer.phone} onChange={(e) => setCustomer({ phone: e.target.value })} /></Field>
              <Field label="Email" wide><Input type="email" value={form.customer.email} onChange={(e) => setCustomer({ email: e.target.value })} /></Field>
            </div>
          </Section>

          <Section title="2. Nội dung đặt hàng">
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
              {(Object.keys(ORDER_TYPE_LABELS) as OrderType[]).map((t) => (
                <label key={t} className={cn('flex items-center gap-1.5', t === 'addon' && !form.orgId && 'opacity-50')}>
                  <input type="radio" name="otype" checked={form.orderType === t} disabled={t === 'addon' && !form.orgId}
                    onChange={() => set({ orderType: t, ...(t === 'addon' && ctx?.effectivePlan ? { planId: ctx.effectivePlan } : {}) })} />
                  {ORDER_TYPE_LABELS[t]}
                </label>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-4">
              <Field label="Gói dịch vụ" className="sm:col-span-2">
                <select className={selectCls} value={form.planId} disabled={form.orderType === 'addon'}
                  onChange={(e) => setForm((f) => applyPlanDefaults(f, plans.find((p) => p.id === e.target.value)))}>
                  {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — {fmt(p.monthlyPrice)} ₫/tháng</option>)}
                  {!plans.some((p) => p.id === form.planId) && form.planId && <option value={form.planId}>{form.planId}</option>}
                </select>
              </Field>
              {form.orderType !== 'addon' ? (
                <>
                  <Field label="Số ngày (30–1.100)"><Input type="number" min={1} max={1100} value={form.days} onChange={(e) => set({ days: e.target.value })} /></Field>
                  <Field label="Từ ngày (dự kiến)"><Input type="date" value={form.startDate} onChange={(e) => set({ startDate: e.target.value })} /></Field>
                </>
              ) : (
                <p className="self-end text-xs text-muted-foreground sm:col-span-2">Mua thêm tính theo số ngày còn lại của kỳ hiện tại{b?.addon ? ` (${b.addon.remainingDays} ngày, tới ${viDate(b.addon.periodEnd)})` : ''}.</p>
              )}
            </div>
            {form.orderType !== 'addon' && (
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <span className="text-muted-foreground">Gợi ý:</span>
                {daySuggestions(pricing).map((d) => (
                  <button key={d.days} type="button" onClick={() => set({ days: String(d.days) })}
                    className={cn('rounded-full border px-2 py-0.5 hover:border-primary', days === d.days && 'border-primary bg-primary/10 text-primary')}>
                    {d.label}
                  </button>
                ))}
                {form.startDate && days > 0 && <span className="ml-2 text-muted-foreground">Đến hết ngày {viDate(endDate(form.startDate, days))}</span>}
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-3">
              {QUOTA_KEYS.map((k) => (
                <Field key={k} label={`${QUOTA_LABELS[k]}${form.orderType === 'addon' ? ' (tổng sau khi mua)' : ''}`}>
                  <Input type="number" min={0} value={form.quantities[k]} onChange={(e) => set({ quantities: { ...form.quantities, [k]: e.target.value } })} />
                </Field>
              ))}
            </div>
            {b && (
              <p className="text-xs text-muted-foreground">
                Gói {b.plan.name} gồm sẵn {QUOTA_KEYS.map((k) => `${b.plan.included[k]} ${QUOTA_LABELS[k].toLowerCase()}`).join(' · ')}
                {' · '}Khách hoạt động / tháng: {b.plan.activeContactsPerMonth == null ? 'không giới hạn' : fmt(b.plan.activeContactsPerMonth)}
                {' · '}AI trả lời / ngày: {b.plan.aiRepliesPerDay == null ? 'không giới hạn' : fmt(b.plan.aiRepliesPerDay)}
              </p>
            )}
          </Section>

          <Section title="Dịch vụ khác (trả một lần: triển khai, đào tạo, tích hợp…)">
            {form.extras.length === 0 && <p className="text-sm text-muted-foreground">Chưa có. VAT áp cùng thuế suất bảng giá.</p>}
            {form.extras.map((e, i) => (
              <div key={i} className="grid grid-cols-[minmax(0,1fr)_80px_140px_32px] items-end gap-2">
                <Field label={i === 0 ? 'Tên dịch vụ' : ''}><Input value={e.name} onChange={(ev) => set({ extras: form.extras.map((x, j) => (j === i ? { ...x, name: ev.target.value } : x)) })} /></Field>
                <Field label={i === 0 ? 'SL' : ''}><Input type="number" min={1} value={e.quantity} onChange={(ev) => set({ extras: form.extras.map((x, j) => (j === i ? { ...x, quantity: ev.target.value } : x)) })} /></Field>
                <Field label={i === 0 ? 'Đơn giá (₫)' : ''}><Input type="number" min={0} step={1000} value={e.unitPrice} onChange={(ev) => set({ extras: form.extras.map((x, j) => (j === i ? { ...x, unitPrice: ev.target.value } : x)) })} /></Field>
                <Button type="button" variant="ghost" size="icon" title="Xoá dòng" onClick={() => set({ extras: form.extras.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => set({ extras: [...form.extras, { name: '', quantity: '1', unitPrice: '' }] })}>
              <Plus className="h-4 w-4" /> Thêm dịch vụ khác
            </Button>
          </Section>

          <Section title="Ưu đãi & điều chỉnh">
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Mã ưu đãi">
                <Input value={form.promoCode} onChange={(e) => set({ promoCode: e.target.value.toUpperCase() })} placeholder="Nếu có" />
              </Field>
              <Field label="Giảm thêm (₫, trước VAT)"><Input type="number" min={0} step={1000} value={form.extraDiscount} onChange={(e) => set({ extraDiscount: e.target.value })} placeholder="0" /></Field>
              <Field label="Lý do giảm thêm"><Input value={form.extraDiscountReason} onChange={(e) => set({ extraDiscountReason: e.target.value })} disabled={!form.extraDiscount} /></Field>
            </div>
            {b?.promoError && <p className="text-xs text-destructive">Mã ưu đãi: {b.promoError}</p>}
            {form.orgId && (
              <div className="space-y-2 rounded-md border border-dashed p-3">
                <div className="flex items-center gap-2">
                  <Checkbox id="mq-override" checked={form.override} onCheckedChange={(v) => set({ override: v === true })} />
                  <Label htmlFor="mq-override" className="font-normal">Ghi đè chính sách mua (hợp đồng đặc biệt: cho hạ gói / giảm số lượng, không quy đổi)</Label>
                </div>
                {form.override && <Input value={form.overrideReason} onChange={(e) => set({ overrideReason: e.target.value })} placeholder="Lý do (ghi nhật ký) — vd số hợp đồng" />}
              </div>
            )}
          </Section>

          <Section title="3. Thanh toán & điều khoản">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Hạn thanh toán"><Input type="date" value={form.dueDate} onChange={(e) => set({ dueDate: e.target.value })} /></Field>
              <Field label="Ghi chú trên báo giá"><Input value={form.note} onChange={(e) => set({ note: e.target.value })} /></Field>
              <Field label="Điều khoản riêng (mỗi dòng một điều — để trống dùng điều khoản của mẫu)" wide>
                <Textarea value={form.terms} onChange={(e) => set({ terms: e.target.value })} placeholder={tpl.terms} className="min-h-[90px]" />
              </Field>
            </div>
            <p className="text-xs text-muted-foreground">Chuyển khoản: {tpl.bankName} · {tpl.bankAccount} · {tpl.bankHolder} (sửa ở tab "Mẫu báo giá").</p>
          </Section>
        </fieldset>

        <aside className="space-y-3 xl:sticky xl:top-2 xl:self-start">
          <PreviewCard b={b} error={preview.error} loading={preview.loading} policyError={isPolicyError} hasOrg={!!form.orgId} />
        </aside>
      </div>

      {paying && existing && <ConfirmPaidDialog quote={existing} onClose={() => setPaying(false)} />}
      {cancelling && existing && <CancelQuoteDialog quote={existing} onClose={() => setCancelling(false)} />}
    </div>
  )
}

function Field({ label, children, wide, className }: { label: string; children: React.ReactNode; wide?: boolean; className?: string }) {
  return (
    <div className={cn('space-y-1', wide && 'sm:col-span-2', className)}>
      {label && <Label className="text-xs text-muted-foreground">{label}</Label>}
      {children}
    </div>
  )
}

function OrgPicker({ orgId, orgName, onPick, onClear }: { orgId: string | null; orgName: string; onPick: (id: string) => void; onClear: () => void }) {
  const [q, setQ] = useState('')
  const [items, setItems] = useState<Awaited<ReturnType<typeof searchQuoteOrgs>>>([])
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => { searchQuoteOrgs(q.trim()).then(setItems).catch(() => setItems([])) }, 250)
    return () => clearTimeout(t)
  }, [q, open])
  if (orgId) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <span>Tổ chức có sẵn:</span>
        <Link to={`/platform/companies/${orgId}`} className="font-medium text-primary hover:underline" target="_blank">{orgName || orgId}</Link>
        <Button type="button" size="sm" variant="ghost" onClick={onClear}><X className="h-4 w-4" /> Khách mới</Button>
      </div>
    )
  }
  return (
    <div className="relative">
      <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
      <Input className="pl-8" value={q} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => setQ(e.target.value)} placeholder="Chọn tổ chức có sẵn (tên / email chủ sở hữu) — bỏ trống nếu khách mới" />
      {open && items.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover shadow-lg">
          {items.map((o) => (
            <button key={o.id} type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
              onMouseDown={(e) => e.preventDefault()} onClick={() => { setOpen(false); setQ(''); onPick(o.id) }}>
              <span className="font-medium">{o.name}</span>
              <span className="ml-2 text-xs text-muted-foreground">{[o.ownerEmail, o.plan, o.expiresAt && `hết hạn ${viDate(o.expiresAt)}`].filter(Boolean).join(' · ')}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function PreviewCard({ b, error, loading, policyError, hasOrg }: {
  b: Breakdown | null; error: { message: string; code?: string } | null; loading: boolean; policyError: boolean; hasOrg: boolean
}) {
  if (error) {
    return (
      <div className="space-y-2 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm">
        <p className="flex items-center gap-2 font-medium text-destructive"><AlertTriangle className="h-4 w-4" /> Chưa tính được giá</p>
        <p>{error.message}</p>
        {policyError && hasOrg && <p className="text-xs text-muted-foreground">Chính sách "mua rồi không trả lại" giống mua trực tuyến. Hợp đồng đặc biệt: bật "Ghi đè chính sách mua" và nhập lý do.</p>}
      </div>
    )
  }
  if (!b) return <div className="rounded-lg border bg-card p-4"><Loading label="Đang tính giá..." /></div>
  const row = (label: React.ReactNode, value: React.ReactNode, cls?: string) => (
    <tr className={cls}><td className="py-1 pr-3">{label}</td><td className="whitespace-nowrap py-1 text-right tabular-nums">{value}</td></tr>
  )
  return (
    <div className={cn('space-y-3 rounded-lg border bg-card p-4 text-sm', loading && 'opacity-60')}>
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-primary">Chi tiết tiền (server tính)</h2>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      <p className="text-xs text-muted-foreground">Cách áp: {MODE_LABELS[b.mode] ?? b.mode}</p>
      <table className="w-full text-xs">
        <thead><tr className="border-b text-muted-foreground"><th className="py-1 text-left font-medium">Hạng mục / tháng</th><th className="text-right font-medium">SL</th><th className="text-right font-medium">Thành tiền</th></tr></thead>
        <tbody>
          {b.items.map((it, i) => (
            <tr key={i} className="border-b last:border-0">
              <td className="py-1 pr-2">{it.label} <span className="text-muted-foreground">({it.description})</span></td>
              <td className="text-right">{it.quantity}</td>
              <td className="text-right tabular-nums">{fmt(it.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="w-full">
        <tbody>
          {row(`Cộng / tháng`, `${fmt(b.perMonth)} ₫`)}
          {row(`Cước ${b.days} ngày (× ${b.days}/30)`, `${fmt(b.subtotal)} ₫`)}
          {b.discount > 0 && row(`Giảm theo thời hạn ${b.discountPercent}%`, `−${fmt(b.discount)} ₫`, 'text-destructive')}
          {b.promo && row(`Mã ưu đãi ${b.promo.code}`, `−${fmt(b.promo.amount)} ₫`, 'text-destructive')}
          {b.credit && row(`Quy đổi ${b.credit.remainingDays} ngày còn lại gói ${b.credit.fromPlanName}`, `−${fmt(b.credit.applied)} ₫`, 'text-destructive')}
          {b.manualDiscount && row(`Giảm thêm (${b.manualDiscount.reason})`, `−${fmt(b.manualDiscount.amount)} ₫`, 'text-destructive')}
          {b.extras.preTax > 0 && row('Dịch vụ khác', `${fmt(b.extras.preTax)} ₫`)}
          {row('Cộng trước thuế', `${fmt(b.preTaxTotal)} ₫`, 'border-t')}
          {row(`Thuế GTGT ${b.vatPercent}%`, `${fmt(b.vatTotal)} ₫`)}
          {row(<b>TỔNG THANH TOÁN</b>, <b className="text-base text-primary">{fmt(b.grandTotal)} ₫</b>, 'border-t-2 border-foreground')}
        </tbody>
      </table>
      <p className="text-xs italic">Bằng chữ: {b.words}.</p>
      {b.extraDays > 0 && <p className="text-xs text-muted-foreground">Cộng thêm {b.extraDays} ngày (quy đổi dư / ngày còn lại) — tổng {b.totalDays} ngày.</p>}
      {b.warnings.length > 0 && (
        <ul className="space-y-1 rounded-md bg-warning/10 p-2 text-xs text-warning">
          {b.warnings.map((w, i) => <li key={i} className="flex gap-1"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{w}</li>)}
        </ul>
      )}
    </div>
  )
}
